import { AssetsProps, UserInfo } from "../types";
import IdentificationIcon from "@/nav-localized/assets/header/identification.svg?react";
import AccountIcon from "@/nav-localized/assets/header/account.svg?react";
import AffiliateIcon from "@/nav-localized/assets/header/affiliate.svg?react";
import ApiIcon from "@/nav-localized/assets/header/api-line.svg?react";
import FeesIcon from "@/nav-localized/assets/header/fees.svg?react";
import CouponIcon from "@/nav-localized/assets/header/nav/coupon.svg?react";
import LogoutIcon from "@/nav-localized/assets/header/log-out.svg?react";
import CopyIcon from "@/nav-localized/assets/header/copy.svg?react";
import SubAccountIcon from "@/nav-localized/assets/header/sub-account.svg?react";
import WarnIcon from "@/nav-localized/assets/header/warn.svg?react";
import UTAIcon from "@/nav-localized/assets/header/UTA-icon.svg?react";
import SettingsIcon from "@/nav-localized/assets/header/settings.svg?react";
import { useStoreState } from "../store/useStore";
import { clipImage } from "../utils";
import { stylesUid, stylesVip, stylesBg } from "./styles";
import {
  dropDownStylesText,
  popperItemHover,
  popperDivider,
  popperButtonBorder,
  megaMenuDismissButton
} from "../styles";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import { Button, useToast } from "@blofin/blofin-ui";
import { useTranslations } from "use-intl";
import useLogout from "../hooks/useLogout";
import { useEffect, useMemo, useState } from "react";
import SwitchDialog from "./switchDialog";
import useAction from "../hooks/useAction";
import { SubAccountResponse } from "./type";
import Cookies from "js-cookie";
// import styles from "./index.module.scss";
import Hot from "../Hot";
import useWatchLocalStorage from "../hooks/useWatchLocalStorage";
import { textNickNameWeight } from "@/nav-localized/config";
import PopOver from "./../PopOver";
import clsx from "clsx";
import styles from "./index.module.scss";

const assets: AssetsProps[] = [
  {
    label: "accountAndSecurity",
    link: "account/overview",
    icon: AccountIcon,
    key: 0,
    auth: (useInfo: UserInfo) => !!useInfo?.sub_allow_login
  },
  {
    label: "identification",
    link: "account/identification",
    icon: IdentificationIcon,
    key: 1
  },
  {
    label: "myCoupons",
    link: "account/coupons",
    icon: CouponIcon,
    key: 2
  },
  {
    label: "affiliate",
    link: "affiliates",
    icon: AffiliateIcon,
    key: 3
  },
  {
    label: "myTradingFees",
    link: "account/trading-fees",
    icon: FeesIcon,
    key: 4
  },
  {
    label: "subAccount",
    link: "account/sub-account",
    icon: SubAccountIcon,
    key: 6,
    isShowInUserGray: true,
    extraIcon: <UTAIcon className="w-[26px] h-[14px] ml-1" />
  },
  {
    label: "api",
    link: "account/apis",
    icon: ApiIcon,
    key: 5,
    isSupportDemoTrading: true,
    auth: () => true
  },
  {
    label: "settings",
    link: "account/settings",
    icon: SettingsIcon,
    key: 7
  }
];

const SECURITY_LEVEL_THRESHOLD = 2;

const OverviewPopper = () => {
  const { userInfo, localeLink, isDemoTrading, theme } = useStoreState();
  const copy = useCopyToClipboard();

  const { methods } = useToast();

  const t = useTranslations("header");

  const { logout } = useLogout();

  const [isUserInGray, setIsUserInGray] = useState(false);

  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);

  const { getSubAccountLists, getUserGray } = useAction();

  const [subAccountInfo, setSubAccountInfo] = useState<SubAccountResponse | null>(null);
  const canSwitch = useMemo(() => {
    return subAccountInfo?.sub_account_list?.some((item) => item.switch_state === 0);
  }, [subAccountInfo]);

  const { value: securityQuantity } = useWatchLocalStorage({
    key: "security_quantity",
    initValue: null
  });

  const copyUid = async () => {
    const res = await copy(userInfo?.uid as string);
    if (res.type === "success") {
      methods.success("success");
    } else {
      methods.danger("error");
    }
  };

  const menus = useMemo(() => {
    let nav = [...assets];

    if (isDemoTrading) {
      nav = nav.filter((item) => item.isSupportDemoTrading);
    }

    if (userInfo?.account_type === "NORMAL_SUB_ACCOUNT") {
      nav = nav.filter((item) => item.auth?.(userInfo));
    }

    if (!isUserInGray) {
      nav = nav.filter((item) => !item.isShowInUserGray);
    }

    return nav;
  }, [isDemoTrading, isUserInGray, userInfo]);

  const getSubAccount = async () => {
    try {
      if (Cookies.get("Global-UserViewType") === "demo-trading") {
        return;
      }
      getSubAccountLists()
        .then((res) => {
          const response = res as unknown as { code: number; data: SubAccountResponse };
          if (response.code === 200) {
            setSubAccountInfo(response.data);
          }
        })
        .catch((error) => {
          console.info(error);
        });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (Cookies.get("Global-UserViewType") !== "demo-trading") {
      getSubAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserGrayState = async () => {
    try {
      getUserGray({ type: 2 }).then((res) => {
        const response = res as unknown as { code: number; data: { gray_flg: boolean } };
        if (response.code === 200) {
          setIsUserInGray(!!response.data.gray_flg);
        }
      });
    } catch (error) {
      console.info(error);
    }
  };

  useEffect(() => {
    if (Cookies.get("Global-UserViewType") !== "demo-trading") {
      if (userInfo?.account_type === "MAIN") {
        getUserGrayState();
      } else {
        setIsUserInGray(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.account_type]);

  return (
    <>
      <PopOver
        trigger="group-hover"
        anchor="top-right"
        containerClassName={"h-[64px] flex items-center"}
        content={
          <div
            className={clsx(stylesBg({ mode: theme }), "overflow-hidden")}
            onMouseEnter={getSubAccount}
          >
            <div
              className={clsx(
                "flex justify-between flex-col",
                theme === "dark" ? styles["scroll-container-dark"] : styles["scroll-container"]
              )}
            >
              <div className="flex justify-between px-[16px] pb-[10px]">
                <div className="flex items-center gap-[12px]">
                  <a href={`${localeLink}/account/overview`}>
                    <img
                      className="w-[40px] h-[40px] rounded-full"
                      src={clipImage(userInfo?.profile, 80, 80)}
                      alt=""
                    />
                  </a>
                  <div
                    className={`${textNickNameWeight} ${dropDownStylesText({
                      mode: theme
                    })}`}
                  >
                    <div
                      dir="ltr"
                      className={`whitespace-nowrap text-[14px] leading-[20px] ${textNickNameWeight} overflow-hidden text-ellipsis w-[126px]`}
                    >
                      {userInfo?.nick_name}
                    </div>
                    <div
                      className={stylesUid({
                        mode: theme
                      })}
                      onClick={copyUid}
                    >
                      <span
                        dir="ltr"
                        className="whitespace-nowrap overflow-hidden text-ellipsis text-[12px] leading-[18px] w-[106px] font-normal"
                      >
                        UID: {userInfo?.uid}
                      </span>
                      <CopyIcon
                        className={clsx(
                          "w-[16px] h-[16px] ml-[6px] cursor-pointer",
                          megaMenuDismissButton({ mode: theme })
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className={stylesVip({
                    mode: theme
                  })}
                >
                  {userInfo?.vip_level === -1 ? t("special") : `VIP${userInfo?.vip_level || 0}`}
                </div>
              </div>
              {userInfo?.switch_able && canSwitch && isUserInGray && (
                <div className="w-full px-[10px] py-[8px] flex justify-center">
                  <Button
                    size="small"
                    theme={theme}
                    variant="ghost"
                    className={clsx("w-[248px] h-[28px]", popperButtonBorder({ mode: theme }))}
                    onClick={() => setSwitchDialogOpen(true)}
                  >
                    {t("swichAccount")}
                  </Button>
                </div>
              )}
              <div className="px-[12px]">
                {menus.map((item, index) => {
                  if (item.label === "api") {
                    return (
                      <a
                        key={index}
                        href={`${localeLink}/${item.link}`}
                        className={clsx(
                          "flex items-center h-[48px] rounded-[8px] px-[12px] text-[14px] font-medium",
                          popperItemHover({ mode: theme }),
                          dropDownStylesText({ mode: theme })
                        )}
                      >
                        <item.icon className="w-[20px] h-[20px] mr-[16px]" />
                        {t(item.label)}
                        {item.hot && <Hot theme={theme} text="New" />}
                      </a>
                    );
                  } else {
                    return (
                      <a
                        key={index}
                        href={`${localeLink}/${item.link}`}
                        className={clsx(
                          "flex items-center h-[48px] rounded-[8px] px-[12px] text-[14px] font-medium",
                          popperItemHover({ mode: theme }),
                          dropDownStylesText({ mode: theme })
                        )}
                      >
                        <item.icon className="w-[20px] h-[20px] mr-[16px]" />
                        {t(item.label)}
                        {item.hot && (
                          <Hot
                            theme={theme}
                            className="w-[26px] !h-[16px] ml-[2px]"
                            text="New"
                            fontSize="text-[20px] scale-50"
                            fontStyle="normal"
                            fontClassName="font-[500]"
                          />
                        )}
                        {item.extraIcon && item.extraIcon}
                        {Number(securityQuantity) < SECURITY_LEVEL_THRESHOLD &&
                          item.label === "accountAndSecurity" && (
                            <WarnIcon className="w-[16px] h-[16px] ml-[2px]" />
                          )}
                      </a>
                    );
                  }
                })}
              </div>
              <div className="px-[12px]">
                <div
                  className={clsx("my-[8px] h-[1px] mx-[12px]", popperDivider({ mode: theme }))}
                ></div>
                <div
                  className={clsx(
                    "flex items-center h-[48px] rounded-[8px] px-[12px] text-[14px] font-medium",
                    popperItemHover({ mode: theme }),
                    dropDownStylesText({ mode: theme })
                  )}
                  onClick={logout}
                >
                  <LogoutIcon className="w-[20px] h-[20px] mr-[16px]" />
                  {t("logout")}
                </div>
              </div>
            </div>
          </div>
        }
      >
        <div className="relative flex-shrink-0">
          <img
            className="w-[36px] h-[36px] cursor-pointer rounded-full"
            src={clipImage(userInfo?.profile, 72, 72)}
          />
        </div>
      </PopOver>

      <SwitchDialog
        open={switchDialogOpen}
        onClose={() => setSwitchDialogOpen(false)}
        subAccountInfo={subAccountInfo}
      />
    </>
  );
};

export default OverviewPopper;
